// ─── STRUCTURED DATA (schema.org / JSON-LD) ─────────────────────────────────
//
// WHY THIS FILE EXISTS
// --------------------
// Prerendering made the site readable to crawlers that do not run JavaScript.
// Structured data is the next layer: it states as machine-readable FACT what a
// crawler would otherwise have to infer from prose. Who this business is, that
// Scott is a real person with a specific expertise, that there is a clinic in
// Auburn Alabama on 3 October with a price and a venue.
//
// This matters disproportionately for AI answers. When someone asks an
// assistant "who does confidence coaching for nervous riders in Virginia", the
// systems that answer lean on explicit entity data far more than on paragraphs.
//
// Schemas used to live inline in five different page files, which meant the
// business address, phone and social profiles existed in several places and
// had already drifted out of date. One definition, imported everywhere.

// Canonical host. Must match SITE_URL in components/seo/SEO.tsx and ORIGIN in
// scripts/gen-sitemap.mjs. See the note in SEO.tsx for why it is www.
const SITE = 'https://www.advantagehorsemanship.com'

/** Stable node ids so schemas can reference each other instead of repeating. */
export const ORG_ID = `${SITE}/#organization`
export const PERSON_ID = `${SITE}/#scott-purdum`

/**
 * Social profiles. `sameAs` is how a search engine or AI ties this website to
 * those accounts and decides they are all the same entity, so these must be
 * the real, current profile URLs.
 *
 * The previous inline copy listed facebook.com/advantagehorsemanship and
 * youtube.com/user/AdvantageHorseman, neither of which matches what the footer
 * actually links to. Tracking parameters are stripped: sameAs wants a stable
 * identifier, not a share link.
 */
const SAME_AS = [
  'https://www.youtube.com/scottpurdum',
  'https://www.instagram.com/advantagehorsemanship',
  'https://www.tiktok.com/@scottpurdum',
  'https://www.facebook.com/advantagehorsemanship/',
]

const POSTAL_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: '129 Hartwood Rd',
  addressLocality: 'Fredericksburg',
  addressRegion: 'VA',
  postalCode: '22406',
  addressCountry: 'US',
}

/** The business. Referenced by every other schema on the site. */
export const AH_ORGANIZATION = {
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'Advantage Horsemanship',
  legalName: 'Advantage Horsemanship LLC',
  url: SITE,
  logo: `${SITE}/icons/icon-512.png`,
  image: `${SITE}/icons/icon-512.png`,
  description:
    'Horse behaviorist and rider confidence coaching by Scott Purdum. In-person clinics, live online classes, and self-paced programs for riders who want to communicate better with their horses.',
  founder: { '@id': PERSON_ID },
  address: POSTAL_ADDRESS,
  telephone: '+1-877-574-6773',
  email: 'info@advantagehorsemanship.com',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-877-574-6773',
    email: 'info@advantagehorsemanship.com',
    contactType: 'customer service',
    areaServed: 'US',
    availableLanguage: 'English',
  },
  // Clinics travel, so the service area is national even though the business
  // address is in Virginia.
  areaServed: { '@type': 'Country', name: 'United States' },
  sameAs: SAME_AS,
}

/** Scott as a person. Separate from the business on purpose: people and
 *  organizations are different entities, and "who is Scott Purdum" is a
 *  question an AI gets asked directly. */
export const SCOTT_PERSON = {
  '@type': 'Person',
  '@id': PERSON_ID,
  name: 'Scott Purdum',
  jobTitle: 'Horse Behaviorist and Rider Confidence Coach',
  url: `${SITE}/about`,
  worksFor: { '@id': ORG_ID },
  description:
    'Scott Purdum is a horse behaviorist and rider confidence coach who has helped over 60,000 riders. He teaches in-person clinics across the United States and coaches riders online, focusing on the communication between horse and rider rather than on isolated training techniques.',
  // knowsAbout is one of the clearest signals available for "what is this
  // person an authority on", and is read directly by AI systems.
  knowsAbout: [
    'Horse behavior',
    'Horsemanship',
    'Rider confidence',
    'Groundwork',
    'Liberty training',
    'Horse and rider communication',
    'Equine training',
    'Colt starting',
  ],
  sameAs: SAME_AS,
}

// ─── BUILDERS ───────────────────────────────────────────────────────────────

export interface ClinicEventInput {
  id: string
  eventName: string
  location: string
  venueName: string
  venueAddress: string
  startDate: Date
  endDate: Date
  durationDays?: 2 | 3
  riderSoldOut: boolean
}

/**
 * Real Event schema, one per scheduled clinic.
 *
 * The previous version was a SINGLE hardcoded Event with no startDate and no
 * location. Google requires both for an event to be eligible for rich results,
 * so it was not just unhelpful, it was invalid. Meanwhile the page already had
 * the real dates, venues and addresses loaded from Firestore and was throwing
 * that information away.
 *
 * Dates are emitted as ISO-8601, which is what schema.org expects.
 */
export function buildClinicEventSchemas(events: ClinicEventInput[]) {
  return events.map((e) => ({
    '@type': 'Event',
    name: e.eventName || 'Riders Foundation Masterclass',
    description: `A ${e.durationDays ?? 3}-day in-person horsemanship intensive with Scott Purdum in ${e.location}. Hands-on coaching, limited to 10 riders. Auditor day passes available.`,
    startDate: e.startDate.toISOString(),
    endDate: e.endDate.toISOString(),
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: e.venueName || e.location,
      address: e.venueAddress
        ? { '@type': 'PostalAddress', streetAddress: e.venueAddress, addressCountry: 'US' }
        : { '@type': 'PostalAddress', addressLocality: e.location, addressCountry: 'US' },
    },
    organizer: { '@id': ORG_ID },
    performer: { '@id': PERSON_ID },
    offers: {
      '@type': 'Offer',
      price: e.durationDays === 2 ? '599.00' : '849.00',
      priceCurrency: 'USD',
      availability: e.riderSoldOut
        ? 'https://schema.org/SoldOut'
        : 'https://schema.org/InStock',
      url: `${SITE}/masterclass`,
      validFrom: new Date().toISOString().slice(0, 10),
    },
  }))
}

/** FAQ markup. Only ever call this with questions and answers that are
 *  genuinely visible on the page: marking up content a visitor cannot see is a
 *  guidelines violation, not a shortcut. */
export function buildFaqSchema(faqs: { q: string; a: string }[]) {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }
}

// ─── COURSES ────────────────────────────────────────────────────────────────

export interface CourseOffer {
  /** Decimal string, e.g. '2500.00'. Schema.org wants a number, not "$2,500". */
  price: string
  /** Shown by Google when several options exist, e.g. 'Full year'. */
  name?: string
}

export interface CourseInput {
  name: string
  description: string
  /** Absolute page URL. */
  url: string
  /** EVERY price the page actually sells. See the note below. */
  offers: CourseOffer[]
  /** Total live sessions, when the run is a fixed length. Omitted if open ended. */
  repeatCount?: number
  image?: string
}

/**
 * Course schema for the live online classes.
 *
 * WHY NOT Product
 * ---------------
 * The Accelerator and Horse Sense pages both declared themselves as
 * `@type: Product`, which tells Google they are physical merchandise. Google
 * then demands shipping cost and a return policy for a Zoom class, cannot
 * satisfy them, and rejects the listing outright. Neither page was eligible for
 * any enhanced result.
 *
 * Course is the type Google built for exactly this, and it has its own rich
 * result. It also drops the `image` requirement that Product imposes, so
 * switching type fixes the rejection on its own, before anybody finds a photo.
 *
 * EVERY PRICE, NOT JUST THE CHEAPEST
 * ----------------------------------
 * Horse Sense sells two options, $500 for two months and $2,500 for the year,
 * and the old schema declared only the $500. Structured data that disagrees
 * with the visible page is a guidelines violation, not merely incomplete, so
 * `offers` takes an array and both go in.
 *
 * NOTHING HERE IS INVENTED. Session length is not declared, because nobody has
 * told me how long a session runs, and a wrong `courseWorkload` is worse than
 * an absent one. Adding it later would strengthen the listing.
 */
export function buildCourseSchema(c: CourseInput) {
  return {
    '@type': 'Course',
    name: c.name,
    description: c.description,
    url: c.url,
    provider: { '@id': ORG_ID },
    ...(c.image ? { image: c.image } : {}),
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'Online',
      instructor: { '@id': PERSON_ID },
      courseSchedule: {
        '@type': 'Schedule',
        repeatFrequency: 'Weekly',
        ...(c.repeatCount ? { repeatCount: c.repeatCount } : {}),
      },
    },
    offers: c.offers.map((o) => ({
      '@type': 'Offer',
      ...(o.name ? { name: o.name } : {}),
      price: o.price,
      priceCurrency: 'USD',
      category: 'Paid',
      availability: 'https://schema.org/InStock',
      url: c.url,
    })),
  }
}
